import React, { useEffect, useState } from 'react';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Paper from '@mui/material/Paper';
import {
    Autocomplete,
    Box,
    Button,
    FormControl,
    Grid,
    IconButton,
    InputLabel,
    MenuItem,
    Select,
    TablePagination,
    TextField,
    Typography
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import Layout from '../../components/Layout/Layout';
import { apiRequest } from '../../utils/api';
import Loader from '../../components/Loader';
import ErrorScreen from '../../components/ErrorScreen';
import moment from 'moment';
import { exportToExcel } from '../../utils/exportToExcel';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { useDispatch, useSelector } from 'react-redux';
import { fetchCustomerData } from '../../redux/slices/customerSlice';
import { fetchRepsData } from '../../redux/slices/repSlice';
import * as XLSX from 'xlsx';
import { productCategories } from '../../assets/data';

export default function Orders() {
    const navigate = useNavigate();
    const dispatch = useDispatch();

    const { data: customers, isLoading: isLoadingCustomers, error: errorCustomers } = useSelector((state) => state.customer);
    const { data: reps, isLoading: isLoadingReps, error: errorReps } = useSelector((state) => state.rep);
    const [goodsList, setGoodsList] = useState([]);

    const presistPage = localStorage.getItem('page');
    const presistRowPerPage = localStorage.getItem('rowPerPage');
    const presistStatus = JSON.parse(localStorage.getItem('status'));
    const presistStartDate = localStorage.getItem('orderStartDate');
    const presistRep = JSON.parse(localStorage.getItem('orderReps'));
    const presistCustomer = JSON.parse(localStorage.getItem('orderCustomers'));
    const presistOrderCategory = JSON.parse(localStorage.getItem('orderCategory'));

    const [page, setPage] = React.useState(presistPage || 0);
    const [rowsPerPage, setRowsPerPage] = React.useState(presistRowPerPage || 10);
    const [count, setCount] = React.useState(0);

    const [orderList, setOrderList] = React.useState(null);
    const [isLoading, setIsLoading] = React.useState(true);
    const [error, setError] = React.useState(null);

    const curDate = new Date();
    const [startDate, setStartDate] = useState(
        presistStartDate
            ? new Date(presistStartDate)?.toLocaleDateString('en-CA')
            : new Date(curDate.getFullYear(), curDate.getMonth(), curDate.getDate() - 2).toLocaleDateString('en-CA')
    );
    const [endDate, setEndDate] = useState(new Date().toLocaleDateString('en-CA'));
    const [dispatchStartDate, setDispatchStartDate] = useState(
        new Date(curDate.getFullYear(), curDate.getMonth(), curDate.getDate() - 2).toLocaleDateString('en-CA')
    );
    const [dispatchEndDate, setDispatchEndDate] = useState(new Date().toLocaleDateString('en-CA'));

    const [selectedRep, setSelectedRep] = useState(presistRep || []);
    const [selectedCustomer, setSelectedCustomer] = useState(presistCustomer || []);
    const [selectedStatus, setSelectedStatus] = React.useState(presistStatus || []);
    const [selectedCategory, setSelectedCategory] = useState(presistOrderCategory || []);
    const [selectedGoods, setSelectedGoods] = useState([]);
    const [selectedVehicleArrive, setSelectedVehicleArrive] = useState('');

    const handleStartDate = (e) => {
        setStartDate(e?.target?.value);
        localStorage.setItem('orderStartDate', e?.target?.value);
    };

    const handleEndDate = (e) => {
        setEndDate(e?.target?.value);
    };

    const isDispatchedSelected = selectedStatus?.includes('Dispatched');
    const isReadyForDispatchSelected = selectedStatus?.includes('Ready for Dispatch');

    const handleChangePage = (event, newPage) => {
        getAllOrders(newPage, rowsPerPage);
        setPage(newPage);
        localStorage.setItem('page', +newPage);
    };

    const handleChangeRowsPerPage = (event) => {
        getAllOrders(page, event.target.value);
        setRowsPerPage(+event.target.value);
        localStorage.setItem('rowPerPage', +event.target.value);
    };

    const getAllOrders = async (pageNumber, pageSize) => {
        try {
            setError(null);
            setOrderList(null);
            setIsLoading(true);

            let repIds = selectedRep?.map((i) => i?.id);
            let customerIds = selectedCustomer?.map((i) => i?.id);

            let data = {
                repId: repIds?.length > 0 ? repIds : null,
                customerId: customerIds?.length > 0 ? customerIds : null,
                status:
                    selectedStatus?.length > 0 ? selectedStatus : ['Order Placed', 'In Process', 'Hold', 'Order Approved', 'Ready for Dispatch'],
                startDate: isDispatchedSelected ? dispatchStartDate : startDate,
                endDate: isDispatchedSelected ? dispatchEndDate : endDate,
                dateFilterField: isDispatchedSelected ? 'dispatchDate' : 'createdOn',
                goods: selectedGoods?.length > 0 ? selectedGoods : null,
                vehicleArrive:
                    selectedVehicleArrive === ''
                        ? null
                        : selectedVehicleArrive === 'yes'
                          ? true
                          : false,
                pageNumber: Number(pageNumber) + 1,
                pageSize: pageSize,
                category: selectedCategory
            };

            const res = await apiRequest('post', `orders/list`, data);

            if (res?.data) {
                setOrderList(res?.data);
                setCount(res?.data?.paginationMetadata?.totalCount);
            }
        } catch (error) {
            setError(error);
        } finally {
            setIsLoading(false);
        }
    };

    React.useEffect(() => {
        if (!customers) {
            dispatch(fetchCustomerData());
        }
        if (!reps) {
            dispatch(fetchRepsData());
        }
        apiRequest('get', 'goods')
            .then((res) => setGoodsList(res?.data || []))
            .catch(() => setGoodsList([]));

        getAllOrders(page, rowsPerPage);

        return () => {
            setError(null);
            setOrderList(null);
            setIsLoading(true);
        };
    }, []);

    const [excelLoad, setExcelLoad] = React.useState(false);

    const handleExport = async () => {
        setExcelLoad(true);
        try {
            const res = await apiRequest('get', 'orders/excel');

            if (res?.data) {
                exportToExcel(res?.data, 'MyFile.xlsx');
            }
        } catch (error) {
            return toast.error(error?.response?.data?.message ?? error?.response?.data ?? error?.message);
        } finally {
            setExcelLoad(false);
        }
    };

    // Function to scroll to the stored position when returning to this page
    const scrollToStoredPosition = () => {
        const presistOrderScrollYIndex = localStorage.getItem('orderScrollYIndex');
        window.scrollTo(0, presistOrderScrollYIndex);
    };

    // Scroll to stored position on component mount
    useEffect(() => {
        scrollToStoredPosition();
    }, [orderList]);

    return (
        <Layout>
            <Grid container sx={{ mb: 2 }} spacing={2} alignItems={'center'}>
                <Grid item lg={12} sm={2} md={2} xs={6}>
                    <Typography variant="h6">Orders</Typography>
                </Grid>

                <Grid
                    onClick={() => navigate(`create`)}
                    item
                    lg={1.5}
                    sm={3.5}
                    md={2.5}
                    xs={6}
                    sx={{ display: { xs: 'block', sm: 'none' } }}
                >
                    <Button fullWidth variant="contained">
                        Create Order
                    </Button>
                </Grid>

                <Grid item lg={1.5} sm={5} md={3} xs={12}>
                    <Autocomplete
                        multiple
                        size="small"
                        options={reps ?? []}
                        defaultValue={[]}
                        getOptionLabel={(rep) => `${rep?.firstName} ${rep?.lastName}`}
                        value={selectedRep || []}
                        onChange={(event, selected) => {
                            setSelectedRep(selected);
                            localStorage.setItem('orderReps', JSON.stringify(selected));
                        }}
                        renderInput={(params) => <TextField {...params} label="Rep" variant="outlined" />}
                    />
                </Grid>

                <Grid item lg={1.5} sm={5} md={3} xs={12}>
                    <Autocomplete
                        multiple
                        size="small"
                        options={customers ?? []}
                        defaultValue={[]}
                        getOptionLabel={(customer) => `${customer?.name}`}
                        value={selectedCustomer || []}
                        onChange={(event, selected) => {
                            setSelectedCustomer(selected);
                            localStorage.setItem('orderCustomers', JSON.stringify(selected));
                        }}
                        renderInput={(params) => <TextField {...params} label="Customer" variant="outlined" />}
                    />
                </Grid>

                <Grid item lg={1.5} sm={5} md={3} xs={12}>
                    <FormControl size="small" fullWidth>
                        <InputLabel id="demo-simple-select-label">Status</InputLabel>
                        <Select
                            label="Status"
                            multiple
                            value={selectedStatus}
                            onChange={(e) => {
                                setSelectedStatus(e.target.value);
                                localStorage.setItem('status', JSON.stringify(e.target.value));
                            }}
                        >
                            <MenuItem value={'Order Placed'}>Order Placed</MenuItem>
                            <MenuItem value={'In Process'}>In Process</MenuItem>
                            <MenuItem value={'Hold'}>Hold</MenuItem>
                            <MenuItem value={'Order Approved'}>Order Approved</MenuItem>
                            <MenuItem value={'Ready for Dispatch'}>Ready for Dispatch</MenuItem>
                            <MenuItem value={'Dispatched'}>Dispatched</MenuItem>
                            <MenuItem value={'Cancelled'}>Cancelled</MenuItem>
                        </Select>
                    </FormControl>
                </Grid>

                <Grid item lg={1.5} sm={5} md={3} xs={12}>
                    <Autocomplete
                        multiple
                        size="small"
                        options={goodsList ?? []}
                        getOptionLabel={(good) => `${good?.name}`}
                        value={goodsList?.filter((i) => selectedGoods?.includes(i?.id)) || []}
                        onChange={(event, selected) => {
                            setSelectedGoods(selected?.map((item) => item?.id));
                        }}
                        renderInput={(params) => <TextField {...params} label="Goods" variant="outlined" />}
                    />
                </Grid>

                <Grid item lg={1.5} sm={5} md={3} xs={12}>
                    <FormControl size="small" fullWidth>
                        <InputLabel id="vehicle-arrive-filter-label">Vehicle Arrive</InputLabel>
                        <Select
                            labelId="vehicle-arrive-filter-label"
                            label="Vehicle Arrive"
                            value={selectedVehicleArrive}
                            onChange={(e) => setSelectedVehicleArrive(e.target.value)}
                        >
                            <MenuItem value={''}>All</MenuItem>
                            <MenuItem value={'yes'}>Yes</MenuItem>
                            <MenuItem value={'no'}>No</MenuItem>
                        </Select>
                    </FormControl>
                </Grid>

                <Grid item lg={1.25} xs={6}>
                    <TextField
                        size="small"
                        fullWidth
                        type="date"
                        variant="outlined"
                        label="Start Date"
                        InputLabelProps={{ shrink: true }}
                        value={startDate}
                        onChange={handleStartDate}
                    />
                </Grid>

                <Grid item lg={1.25} xs={6}>
                    <TextField
                        size="small"
                        fullWidth
                        type="date"
                        variant="outlined"
                        label="End Date"
                        InputLabelProps={{ shrink: true }}
                        value={endDate}
                        onChange={handleEndDate}
                    />
                </Grid>

                {isDispatchedSelected && (
                    <Grid item lg={1.25} xs={6}>
                        <TextField
                            size="small"
                            fullWidth
                            type="date"
                            variant="outlined"
                            label="Dispatch Start Date"
                            InputLabelProps={{ shrink: true }}
                            value={dispatchStartDate}
                            onChange={(e) => setDispatchStartDate(e.target.value)}
                        />
                    </Grid>
                )}

                {isDispatchedSelected && (
                    <Grid item lg={1.25} xs={6}>
                        <TextField
                            size="small"
                            fullWidth
                            type="date"
                            variant="outlined"
                            label="Dispatch End Date"
                            InputLabelProps={{ shrink: true }}
                            value={dispatchEndDate}
                            onChange={(e) => setDispatchEndDate(e.target.value)}
                        />
                    </Grid>
                )}

                <Grid item lg={1} sm={3.5} md={2.5} xs={12}>
                    <Button
                        onClick={() => getAllOrders(0, rowsPerPage)}
                        fullWidth
                        variant="contained"
                        sx={{ textTransform: 'capitalize', bgcolor: 'green' }}
                    >
                        Filter
                    </Button>
                </Grid>

                <Grid item lg={1.5} sm={3.5} md={2.5} xs={12} sx={{ display: { xs: 'none', sm: 'block' } }}>
                    <Button
                        disabled={excelLoad}
                        onClick={() => handleExport()}
                        fullWidth
                        variant="contained"
                        sx={{ textTransform: 'capitalize', bgcolor: 'green' }}
                    >
                        Export to Excel
                    </Button>
                </Grid>

                <Grid item lg={1} sm={3.5} md={2.5} xs={12} sx={{ display: { xs: 'none', sm: 'block' } }}>
                    <Button fullWidth variant="contained" sx={{ textTransform: 'capitalize', bgcolor: 'green' }}>
                        Print
                    </Button>
                </Grid>

                <Grid item lg={1.5} sm={3.5} md={2.5} xs={12} sx={{ display: { xs: 'none', sm: 'block' } }}>
                    <Button onClick={() => navigate(`create`)} fullWidth variant="contained">
                        Create Order
                    </Button>
                </Grid>

                <Grid item lg={2} sm={5} md={3} xs={12}>
                    <Autocomplete
                        multiple
                        size="small"
                        options={productCategories}
                        getOptionLabel={(cat) => cat}
                        value={selectedCategory}
                        onChange={(event, selected) => {
                            setSelectedCategory(selected);
                            localStorage.setItem('orderCategory', JSON.stringify(selected));
                        }}
                        renderInput={(params) => <TextField {...params} label="Category" variant="outlined" />}
                    />
                </Grid>
            </Grid>

            <TableContainer component={Paper}>
                <Table sx={{ minWidth: 650 }} size="small" aria-label="a dense table">
                    <TableHead>
                        <TableRow>
                            <TableCell>ID</TableCell>
                            <TableCell>Rep Name</TableCell>
                            <TableCell>Customer Name</TableCell>
                            <TableCell>Total Prod</TableCell>
                            <TableCell>Status</TableCell>
                            {isReadyForDispatchSelected && <TableCell>Vehicle Arrive Date</TableCell>}
                            {(isReadyForDispatchSelected || isDispatchedSelected) && <TableCell>Goods</TableCell>}
                            <TableCell>Comments</TableCell>
                            <TableCell>Created Date</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {isLoading && (
                            <TableCell colSpan={10}>
                                <Loader />
                            </TableCell>
                        )}
                        {!!orderList &&
                            orderList?.items?.map((row) => (
                                <TableRow
                                    onClick={() => {
                                        localStorage.setItem('orderScrollYIndex', +window.scrollY);
                                        window.scrollTo(0, 0);
                                        navigate(`detail/${row?.id}`, { state: row });
                                        // const orderUrl = `/orders/detail/${row?.id}`;
                                        // window.open(orderUrl, '_blank');
                                    }}
                                    key={row.name}
                                    sx={{
                                        '&:last-child td, &:last-child th': { border: 0 },
                                        '&:hover': { bgcolor: '#f0f0f0', cursor: 'pointer' }
                                    }}
                                >
                                    <TableCell component="th" scope="row">
                                        {row.serialNo}
                                    </TableCell>
                                    <TableCell>
                                        {row?.user?.firstName} {row?.user?.lastName}
                                    </TableCell>
                                    <TableCell>{row?.customer?.name}</TableCell>
                                    <TableCell>{row?.totalProducts}</TableCell>
                                    <TableCell>{row?.status}</TableCell>
                                    {isReadyForDispatchSelected && (
                                        <TableCell>
                                            {row?.vehicleArriveDate ? moment(row?.vehicleArriveDate).format('MMM DD, YYYY - hh:mm A') : '-'}
                                        </TableCell>
                                    )}
                                    {(isReadyForDispatchSelected || isDispatchedSelected) && (
                                        <TableCell>
                                            {row?.goods?.length > 0 ? row?.goods?.map((good) => good?.name).join(', ') : '-'}
                                        </TableCell>
                                    )}
                                    <TableCell>
                                        {row?.comments?.slice(0, 20)} {row?.comments?.length > 20 && '...'}
                                    </TableCell>
                                    <TableCell>{moment(row?.createdOn).format('MMM DD, YYYY - hh:mm A')}</TableCell>
                                </TableRow>
                            ))}
                        {!!error && (
                            <TableCell colSpan={10}>
                                <ErrorScreen error={error} />
                            </TableCell>
                        )}
                    </TableBody>
                </Table>
            </TableContainer>

            {/* <TablePagination
                rowsPerPageOptions={[5, 10, 20]}
                component="div"
                count={count}
                rowsPerPage={rowsPerPage}
                page={page}
                onPageChange={handleChangePage}
                onRowsPerPageChange={handleChangeRowsPerPage}
            /> */}
        </Layout>
    );
}
